/**
 * ImageUploadPicker — shared manual-image picker used by every form that
 * accepts a user-uploaded image (CreatePostPage, EditPostPage, and the
 * Cover Image section of CreateChallengePage).
 *
 * Owns:
 *   - Hidden <input type="file"> + visible "Gallery" trigger button.
 *   - MIME whitelist (jpeg / jpg / png / gif / webp).
 *   - 10MB size cap.
 *   - FileReader-driven base64 preview (fires as soon as the file is picked
 *     so callers can render a preview without waiting for the upload).
 *   - POST /api/upload with progress reporting.
 *
 * Does NOT own:
 *   - Preview rendering. Every caller has its own preview layout
 *     (position, size, remove button, AI-preview overlay) — this
 *     component just hands the caller the data-URL for local preview
 *     and, once uploaded, the server URL.
 *   - AI generation. Callers keep their own AI logic; this component is
 *     the "manual" side only.
 *
 * Behaviour: on file select, the picker uploads immediately (not at form
 * submit time). This is a small semantic move from the previous inline
 * pattern in CreatePostPage/EditPostPage where the upload happened at
 * submit — the net upload endpoint, payload, and returned URL are the
 * same. Callers store the URL and use it directly at submit.
 *
 * Props:
 *   label            — text on the trigger button. Default "Gallery".
 *   disabled         — disables the button (e.g. while AI is running).
 *   onUploadStart    — fires with the base64 preview immediately after
 *                      validation. Optional. Useful for showing the
 *                      picked image before the upload finishes.
 *   onUploadComplete — fires with the server URL when POST /api/upload
 *                      resolves. Required.
 *   onUploadError    — fires with the error message. Optional; picker
 *                      also emits a toast via notify.
 *   onProgress       — fires with 0..100 during upload. Optional.
 *   uploader         — optional replacement for the default POST /api/upload
 *                      call. Signature: `(formData, { onUploadProgress }) =>
 *                      Promise<{ url }>`. Used by callers whose auth token
 *                      lives outside the default axios instance (e.g. the
 *                      Superadmin dashboard, which uses its own token store).
 *                      If not provided, the picker POSTs via the shared
 *                      axios util as before.
 *   testId           — data-testid on the trigger button. Default
 *                      "gallery-upload-btn".
 */
import React, { useRef } from 'react';
import { ImagePlus } from 'lucide-react';
import axios, { API } from '../../utils/api';
import notify from '../../utils/notify';
import { PHOTO_ACCEPT_WITH_GIF, HEIC_MESSAGE, isHeic } from '../../utils/imageFile';

const ALLOWED_TYPES = ['image/jpeg', 'image/jpg', 'image/png', 'image/gif', 'image/webp'];
const MAX_BYTES = 10 * 1024 * 1024;
const TEAL = '#4DB6AC';

const ImageUploadPicker = ({
  label = 'Gallery',
  disabled = false,
  onUploadStart,
  onUploadComplete,
  onUploadError,
  onProgress,
  uploader,
  testId = 'gallery-upload-btn',
  style = {},
}) => {
  const inputRef = useRef(null);
  const inputId = React.useMemo(() => `image-upload-${Math.random().toString(36).slice(2, 9)}`, []);

  const handleChange = async (e) => {
    const file = e.target.files?.[0];
    // Clear the input so picking the same file twice in a row still fires
    // change. (Standard file-input gotcha.)
    e.target.value = '';
    if (!file) return;

    if (isHeic(file)) {
      notify.error(HEIC_MESSAGE);
      onUploadError?.('HEIC not supported');
      return;
    }
    if (!ALLOWED_TYPES.includes(file.type)) {
      notify.error('Please upload an image (JPG, PNG, GIF, WEBP)');
      onUploadError?.('Invalid file type');
      return;
    }
    if (file.size > MAX_BYTES) {
      notify.error('File size must be less than 10MB');
      onUploadError?.('File too large');
      return;
    }

    // Base64 preview — fires immediately so the caller can render the
    // image while the upload is still in flight.
    if (onUploadStart) {
      const reader = new FileReader();
      reader.onloadend = () => onUploadStart(reader.result);
      reader.readAsDataURL(file);
    }

    // Actual upload
    try {
      const fd = new FormData();
      fd.append('file', file);
      const onUploadProgress = onProgress
        ? (p) => onProgress(Math.round((p.loaded * 100) / (p.total || 1)))
        : undefined;
      let url;
      if (uploader) {
        const result = await uploader(fd, { onUploadProgress });
        url = result?.url;
      } else {
        const res = await axios.post(`${API}/upload`, fd, {
          headers: { 'Content-Type': 'multipart/form-data' },
          onUploadProgress,
        });
        url = res.data.url;
      }
      onUploadComplete?.(url);
    } catch (err) {
      const msg = err?.response?.data?.detail || err?.message || 'Upload failed';
      notify.error('Failed to upload image: ' + msg);
      onUploadError?.(msg);
    }
  };

  return (
    <>
      <input
        ref={inputRef}
        id={inputId}
        type="file"
        accept={PHOTO_ACCEPT_WITH_GIF}
        onChange={handleChange}
        style={{ display: 'none' }}
        data-testid={`${testId}-input`}
      />
      <button
        type="button"
        data-testid={testId}
        onClick={() => !disabled && inputRef.current?.click()}
        disabled={disabled}
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          gap: 8,
          padding: '12px 16px',
          background: disabled ? '#F9FAFB' : '#F3F4F6',
          color: disabled ? '#9CA3AF' : '#374151',
          border: '1.5px solid #E5E7EB',
          borderRadius: 12,
          fontSize: 14,
          fontWeight: 600,
          cursor: disabled ? 'not-allowed' : 'pointer',
          transition: 'all 0.2s',
          ...style,
        }}
        onMouseEnter={(e) => {
          if (disabled) return;
          e.currentTarget.style.borderColor = TEAL;
          e.currentTarget.style.color = TEAL;
        }}
        onMouseLeave={(e) => {
          if (disabled) return;
          e.currentTarget.style.borderColor = '#E5E7EB';
          e.currentTarget.style.color = '#374151';
        }}
      >
        <ImagePlus width={18} height={18} /> {label}
      </button>
    </>
  );
};

export default ImageUploadPicker;
